import { useCallback, useEffect, useRef } from 'react'

import { useNavigate } from '@lib/react-router-dom'

import type { MasterTransaction } from '@model/MasterTransaction'

import { safeLocalStorage } from '@hooks/useStorage'

import {
  THREE_DS_CALLBACK_READY_STORAGE_KEY,
  THREE_DS_MESSAGE_AUTH_FAILED,
  THREE_DS_MESSAGE_DEPOSIT_FAILED,
  THREE_DS_MESSAGE_DEPOSIT_RESULT,
} from '../../constants/threeDS'

type ThreeDSMessage =
  | {
      type: typeof THREE_DS_MESSAGE_DEPOSIT_RESULT
      data?: MasterTransaction
      values?: any
      walletId?: string
      reason?: string
    }
  | { type: typeof THREE_DS_MESSAGE_AUTH_FAILED; reason?: string }
  | { type: typeof THREE_DS_MESSAGE_DEPOSIT_FAILED; reason?: string }
  | { type?: string; data?: MasterTransaction; values?: any; walletId?: string; reason?: string }

export const useThreeDSPostMessage = (params: {
  onDepositResult: (tx: MasterTransaction, txValues: any) => void
  onAuthFailed: (reason?: string) => void
  onDepositFailed: () => void
}) => {
  const { onDepositResult, onAuthFailed, onDepositFailed } = params
  const navigate = useNavigate()

  const handlerRef = useRef<(event: MessageEvent) => void>(() => undefined)

  useEffect(() => {
    handlerRef.current = (event: MessageEvent) => {
      if (event.origin !== window.location.origin) return

      const msg = (event.data || {}) as ThreeDSMessage
      if (!msg?.type) return

      if (msg.type === THREE_DS_MESSAGE_DEPOSIT_RESULT && msg.data && msg.values) {
        onDepositResult(msg.data, msg.values)
        return
      }

      if (msg.type === THREE_DS_MESSAGE_AUTH_FAILED) {
        onAuthFailed(msg.reason)
        return
      }

      if (msg.type === THREE_DS_MESSAGE_DEPOSIT_FAILED) {
        onDepositFailed()
        return
      }
    }
  }, [onAuthFailed, onDepositFailed, onDepositResult])

  const redirectToThreeDSCallback = useCallback(
    (search = '') => {
      const params = new URLSearchParams(search.startsWith('?') ? search.slice(1) : search)
      params.delete('target')
      const query = params.toString()
      navigate(`/deposit/3ds/callback${query ? `?${query}` : ''}`)
    },
    [navigate]
  )

  const consumeStoredThreeDSCallbackReady = useCallback(
    (search = '') => {
      safeLocalStorage.removeItem(THREE_DS_CALLBACK_READY_STORAGE_KEY)
      redirectToThreeDSCallback(search)
    },
    [redirectToThreeDSCallback]
  )

  const handleStorageEvent = useCallback(
    (event: StorageEvent) => {
      if (event.key !== THREE_DS_CALLBACK_READY_STORAGE_KEY || !event.newValue) return

      try {
        const value = JSON.parse(event.newValue) as { search?: string }
        consumeStoredThreeDSCallbackReady(value.search || '')
      } catch {
        consumeStoredThreeDSCallbackReady('')
      }
    },
    [consumeStoredThreeDSCallbackReady]
  )

  useEffect(() => {
    const persisted = safeLocalStorage.getJSON<{ search?: string }>(
      THREE_DS_CALLBACK_READY_STORAGE_KEY
    )
    if (persisted) {
      consumeStoredThreeDSCallbackReady(persisted.search || '')
    }
  }, [consumeStoredThreeDSCallbackReady])

  useEffect(() => {
    const handler = (e: MessageEvent) => handlerRef.current(e)

    window.addEventListener('message', handler)
    window.addEventListener('storage', handleStorageEvent)

    return () => {
      window.removeEventListener('message', handler)
      window.removeEventListener('storage', handleStorageEvent)
    }
  }, [handleStorageEvent])
}
